import {
  createAlcoholCurve,
  drinkingDayWindow,
  estimateAlcoholAt,
  fromZonedDateTimeLocalValue,
  toZonedDateTimeLocalValue,
  type AlcoholModelProfile,
  type CreateDrinkEventRequest,
  type ConsumptionEvent,
  type DrinkEvent,
  type GroupInvitation,
} from '@lbc/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { lazy, Suspense, useCallback, useEffect, useRef, useState, type FocusEvent } from 'react';

import { Logo } from '../../components/Logo';
import { api, ApiClientError } from '../../services/api/client';
import {
  consumptionOutbox,
  drinkMutationQueue,
  eventQueue,
  type PendingDrinkMutation,
} from '../../services/persistence/event-queue';
import { bootstrapCache } from '../../services/persistence/bootstrap-cache';
import { identityStore, installationStore } from '../../services/persistence/identity-store';
import { AchievementsList } from '../achievements/AchievementsList';
import { ActivityFeed } from '../feed/ActivityFeed';
import { GroupViewSelector } from '../groups/GroupViewSelector';
import { preparePhoto } from '../photos/photo-processing';
import { PhotoViewer } from '../photos/PhotoViewer';
import { PhotoStorageAdminPanel } from '../photos/PhotoStorageAdminPanel';
import { SettingsPanel } from '../settings/SettingsPanel';
import {
  createWrappedMonthlyComparison,
  isAnnualWrappedAvailable,
  isMonthlyWrappedAvailable,
  wrappedAnnualPeriodKey,
  wrappedButtonLabel,
  wrappedMonthlyTargetMonthKey,
} from '../wrapped/wrapped-model';
import { DailyCrown } from './DailyCrown';
import { createDailyStandings, isCrownRank } from './daily-standings';
import { useDashboardSyncPending } from './use-dashboard-sync-pending';
import {
  createCounterDrinks,
  describeDrink,
  estimateDrinkKilocalories,
  GLOBAL_DEFAULT_BEER_ABV,
  isCocktailPreset,
  type CounterDrinkId,
} from './drink-presets';

interface DashboardPageProps {
  onExit: () => void;
  onAddGroup?: () => void;
  onOpenWrapped?: () => void;
  onIdentityChange?: (tab?: 'today' | 'group') => void;
  initialTab?: 'today' | 'group';
  initialPhotoId?: string | null;
  onClosePhoto?: () => void;
}

interface ProfileSettings {
  weightKg: number;
  heightCm?: number | null | undefined;
  notificationsEnabled?: boolean | undefined;
}

const GroupHistoryChart = lazy(async () => ({
  default: (await import('../history/GroupHistoryChart')).GroupHistoryChart,
}));

export function DashboardPage({
  onExit,
  onAddGroup = () => undefined,
  onOpenWrapped = () => undefined,
  onIdentityChange = () => undefined,
  initialTab = 'today',
  initialPhotoId = null,
  onClosePhoto = () => undefined,
}: DashboardPageProps) {
  const [installation] = useState(() => installationStore.read());
  const [selectedMembership] = useState(() => installationStore.selectedMembership());
  const [identity] = useState(() => selectedMembership?.legacyIdentity ?? identityStore.read());
  const [profile, setProfile] = useState<ProfileSettings | null>(
    () => installation?.profile ?? null,
  );
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<CreateDrinkEventRequest[]>([]);
  const [pendingMutations, setPendingMutations] = useState<PendingDrinkMutation[]>([]);
  const [announcement, setAnnouncement] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [lastAddedId, setLastAddedId] = useState<string | null>(null);
  const [selectedMeasurements, setSelectedMeasurements] = useState<
    Record<
      CounterDrinkId,
      {
        volumeMl: number;
        abv: number;
      }
    >
  >({
    beer: { volumeMl: 500, abv: GLOBAL_DEFAULT_BEER_ABV },
    wine: { volumeMl: 100, abv: 12 },
    cocktail: { volumeMl: 60, abv: 40 },
  });
  const [quantityPicker, setQuantityPicker] = useState<CounterDrinkId | null>(null);
  const [cocktailVolumeDraft, setCocktailVolumeDraft] = useState('6');
  const [cocktailAbvDraft, setCocktailAbvDraft] = useState('40');
  const [addingDrink, setAddingDrink] = useState(false);
  const addingDrinkRef = useRef(false);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [editingOccurredAt, setEditingOccurredAt] = useState('');
  const [editingVolumeMl, setEditingVolumeMl] = useState('');
  const [editingAbv, setEditingAbv] = useState('');
  const [editingError, setEditingError] = useState('');
  const [eventHistoryError, setEventHistoryError] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [activeTab, setActiveTab] = useState<'today' | 'group'>(initialTab);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [feedOpen, setFeedOpen] = useState(false);
  const [feedUnreadCount, setFeedUnreadCount] = useState(0);
  const [groupNameDraft, setGroupNameDraft] = useState(selectedMembership?.displayName ?? '');
  const [groupNameBusy, setGroupNameBusy] = useState(false);
  const [invitation, setInvitation] = useState<GroupInvitation | null>(null);
  const [invitationBusy, setInvitationBusy] = useState(false);
  const [clock, setClock] = useState(() => Date.now());
  const [deletedEvent, setDeletedEvent] = useState<DrinkEvent | null>(null);
  const [complimentVisible, setComplimentVisible] = useState(false);
  const [wrappedPromptHiddenPeriod, setWrappedPromptHiddenPeriod] = useState<string | null>(null);
  const [photoOffer, setPhotoOffer] = useState<{ eventId: string; label: string } | null>(null);
  const [photoSelectionStarted, setPhotoSelectionStarted] = useState(false);
  const [photoOfferExpiresAt, setPhotoOfferExpiresAt] = useState<number | null>(null);
  const [photoCountdownSeconds, setPhotoCountdownSeconds] = useState(60);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [dismissedPhotoId, setDismissedPhotoId] = useState<string | null>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const libraryInput = useRef<HTMLInputElement>(null);
  const touchStart = useRef<{ y: number; atTop: boolean; atBottom: boolean } | null>(null);
  const complimentTimer = useRef<number | null>(null);
  const deletedEventTimer = useRef<number | null>(null);

  const cachedBootstrap = useState(() =>
    identity === null ? null : bootstrapCache.read(identity.groupId),
  )[0];
  const deepPhotoQuery = useQuery({
    queryKey: ['photo', initialPhotoId],
    queryFn: () => api.photo(initialPhotoId as string),
    enabled: initialPhotoId !== null && dismissedPhotoId !== initialPhotoId,
    staleTime: 30_000,
  });

  useEffect(() => {
    return () => {
      if (complimentTimer.current !== null) {
        window.clearTimeout(complimentTimer.current);
      }
      if (deletedEventTimer.current !== null) {
        window.clearTimeout(deletedEventTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (photoOffer === null || photoSelectionStarted || photoOfferExpiresAt === null) return;
    const updateCountdown = () => {
      const remaining = Math.max(0, Math.ceil((photoOfferExpiresAt - Date.now()) / 1_000));
      setPhotoCountdownSeconds(remaining);
      if (remaining === 0) {
        setPhotoOffer(null);
        setPhotoOfferExpiresAt(null);
      }
    };
    updateCountdown();
    const timer = window.setInterval(updateCountdown, 1_000);
    return () => window.clearInterval(timer);
  }, [photoOffer, photoOfferExpiresAt, photoSelectionStarted]);

  useEffect(() => {
    if (invitation === null) {
      return;
    }
    const timer = window.setInterval(() => setClock(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, [invitation]);

  useEffect(() => {
    const timer = window.setInterval(() => setClock(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  function showCompliment() {
    setComplimentVisible(true);
    if (complimentTimer.current !== null) {
      window.clearTimeout(complimentTimer.current);
    }
    complimentTimer.current = window.setTimeout(() => {
      setComplimentVisible(false);
      complimentTimer.current = null;
    }, 4_000);
  }

  const bootstrapQuery = useQuery({
    queryKey: ['bootstrap', identity?.groupId],
    queryFn: () =>
      installation === null || selectedMembership === null
        ? api.bootstrap()
        : api.groupBootstrap(selectedMembership.groupId),
    enabled: identity !== null,
    staleTime: 0,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
    ...(cachedBootstrap === null || isOnline
      ? {}
      : {
          initialData: cachedBootstrap.data,
          initialDataUpdatedAt: cachedBootstrap.savedAt,
        }),
  });
  const personalConsumptionsQuery = useQuery({
    queryKey: ['consumptions', installation?.installationId],
    queryFn: () => api.consumptions(),
    enabled: installation !== null && identity !== null,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });
  const photoUploadStatusQuery = useQuery({
    queryKey: ['photo-upload-status', bootstrapQuery.data?.group.id],
    queryFn: () => api.photoUploadStatus(bootstrapQuery.data?.group.id as string),
    enabled: photoOffer !== null && bootstrapQuery.data !== undefined && installation !== null,
    staleTime: 15_000,
  });

  useEffect(() => {
    if (identity !== null && bootstrapQuery.data !== undefined) {
      identityStore.write(identity, bootstrapQuery.data.group.name);
      bootstrapCache.write(bootstrapQuery.data);
    }
  }, [bootstrapQuery.data, identity]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (installation === null || bootstrapQuery.data === undefined) return;
    let active = true;
    const installationId = installation.installationId;
    const checkFeed = async () => {
      const range = feedDayRange(bootstrapQuery.data.group.timezone);
      const seenAt = readFeedSeenAt(installationId);
      try {
        const result = await api.activityFeed(range.from, range.to, seenAt ?? undefined);
        if (!active) return;
        if (seenAt === null) {
          const latest = latestFeedCreatedAt(result.entries);
          if (latest !== undefined) writeFeedSeenAt(installationId, latest);
        } else if (result.hasNew) {
          setFeedUnreadCount(
            result.entries.filter((entry) => entry.event.createdAt > seenAt).length,
          );
        }
      } catch {
        // A failed refresh must retain the last confirmed seen state and current badge.
      }
    };
    void checkFeed();
    const timer = window.setInterval(() => void checkFeed(), 30_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [bootstrapQuery.data, installation]);

  const acknowledgeFeed = useCallback(
    (latestCreatedAt: string | undefined) => {
      if (installation !== null && latestCreatedAt !== undefined) {
        writeFeedSeenAt(installation.installationId, latestCreatedAt);
      }
      setFeedUnreadCount(0);
    },
    [installation],
  );

  const announceSyncError = useCallback(
    (error: unknown) => setAnnouncement(readableDashboardError(error)),
    [],
  );
  const syncPending = useDashboardSyncPending({
    identity,
    installation,
    isOnline,
    queryClient,
    setPending,
    setPendingMutations,
    setSyncing,
    isRetryableError: isRetryableSyncError,
    onSyncError: announceSyncError,
  });

  useEffect(() => {
    void Promise.all([
      installation === null
        ? eventQueue.list()
        : consumptionOutbox.list().then((commands) => commands.map((item) => item.event)),
      drinkMutationQueue.list(),
    ]).then(([queuedEvents, mutations]) => {
      setPending(queuedEvents);
      setPendingMutations(
        mutations.filter(
          (mutation) =>
            identity !== null &&
            mutation.groupId === identity.groupId &&
            mutation.participantId === identity.participantId,
        ),
      );
    });
  }, [identity, installation]);

  useEffect(() => {
    const handleOnline = () => void syncPending();
    const handleFocus = () => void syncPending();
    window.addEventListener('online', handleOnline);
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('focus', handleFocus);
    };
  }, [syncPending]);

  useEffect(() => {
    if (isOnline) {
      void syncPending();
    }
  }, [isOnline, syncPending]);

  const refreshAll = useCallback(async () => {
    if (!isOnline || identity === null) {
      setAnnouncement('Die Aktualisierung braucht eine Internetverbindung.');
      return;
    }
    try {
      await syncPending();
      await queryClient.refetchQueries({ queryKey: ['bootstrap'], type: 'active' });
      setAnnouncement('Alle Gruppendaten wurden aktualisiert.');
    } catch (error) {
      setAnnouncement(
        error instanceof Error
          ? error.message
          : 'Die Gruppendaten konnten gerade nicht aktualisiert werden.',
      );
    }
  }, [identity, isOnline, queryClient, syncPending]);

  useEffect(() => {
    const handleTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      if (touch === undefined) {
        return;
      }
      const scrollTop = Math.max(0, window.scrollY);
      const scrollBottom = scrollTop + window.innerHeight;
      touchStart.current = {
        y: touch.clientY,
        atTop: scrollTop <= 1,
        atBottom: scrollBottom >= document.documentElement.scrollHeight - 2,
      };
    };
    const handleTouchEnd = (event: TouchEvent) => {
      const start = touchStart.current;
      const touch = event.changedTouches[0];
      touchStart.current = null;
      if (start === null || touch === undefined) {
        return;
      }
      const deltaY = touch.clientY - start.y;
      const pulledAtTop = start.atTop && deltaY > 64;
      const pulledAtBottom = start.atBottom && deltaY < -64;
      if (pulledAtTop || pulledAtBottom) {
        void refreshAll();
      }
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [refreshAll]);

  if (identity === null) {
    return null;
  }

  const bootstrap = bootstrapQuery.data;
  const isPhotoStorageAdmin =
    bootstrap !== undefined &&
    bootstrap.group.name === 'Buberkurva' &&
    bootstrap.participant.displayName === 'RT' &&
    bootstrap.identity.role === 'admin';
  const drinkingWindow =
    bootstrap === undefined
      ? null
      : drinkingDayWindow(
          new Date(clock),
          bootstrap.group.timezone,
          bootstrap.group.dayBoundaryHour,
        );
  const groupCombinedEvents = mergeEvents(
    bootstrap?.drinkEvents.filter((event) => event.deletedAt === null) ?? [],
    pending,
    pendingMutations,
    identity.groupId,
    identity.participantId,
  );
  const groupOwnEvents = groupCombinedEvents.filter(
    (event) => event.participantId === identity.participantId,
  );
  const personalSyncedEvents =
    installation === null
      ? (bootstrap?.drinkEvents.filter(
          (event) => event.deletedAt === null && event.participantId === identity.participantId,
        ) ?? [])
      : personalConsumptionsQuery.data !== undefined
        ? personalConsumptionsQuery.data
            .filter((event) => event.deletedAt === null)
            .flatMap((event) =>
              personalDrinkEvents(
                event,
                identity.groupId,
                identity.participantId,
                bootstrap?.group,
              ),
            )
        : (bootstrap?.drinkEvents.filter(
            (event) => event.deletedAt === null && event.participantId === identity.participantId,
          ) ?? []);
  const personalCombinedEvents = mergeEvents(
    personalSyncedEvents,
    pending,
    pendingMutations,
    identity.groupId,
    identity.participantId,
  );
  const activeEvents = groupCombinedEvents.filter((event) => {
    const occurredAt = new Date(event.occurredAt).getTime();
    return (
      drinkingWindow !== null &&
      occurredAt >= drinkingWindow.start.getTime() &&
      occurredAt <= drinkingWindow.end.getTime()
    );
  });
  const personalActiveEvents = personalCombinedEvents.filter((event) => {
    const occurredAt = new Date(event.occurredAt).getTime();
    return (
      drinkingWindow !== null &&
      occurredAt >= drinkingWindow.start.getTime() &&
      occurredAt <= drinkingWindow.end.getTime()
    );
  });
  const ownEvents = personalActiveEvents;
  const allOwnEvents = personalCombinedEvents;
  const ownKilocalories = ownEvents.reduce(
    (total, event) => total + estimateDrinkKilocalories(event),
    0,
  );
  const recentOwnEvents = personalCombinedEvents
    .sort((left, right) => eventRecency(right) - eventRecency(left))
    .slice(0, 10);
  const ownCount = ownEvents.length;
  const calculatedNow = new Date(clock);
  const alcoholProfile: AlcoholModelProfile | null =
    bootstrap?.participant.weightKg === undefined
      ? null
      : {
          weightKg: bootstrap.participant.weightKg,
          ...(bootstrap.participant.heightCm === undefined
            ? {}
            : { heightCm: bootstrap.participant.heightCm }),
          ...(bootstrap.participant.distributionFactor === null ||
          bootstrap.participant.distributionFactor === undefined
            ? {}
            : { distributionFactor: bootstrap.participant.distributionFactor }),
          ...(bootstrap.participant.eliminationRate === null ||
          bootstrap.participant.eliminationRate === undefined
            ? {}
            : { eliminationRate: bootstrap.participant.eliminationRate }),
        };
  const estimate =
    alcoholProfile === null ? 0 : estimateAlcoholAt(allOwnEvents, alcoholProfile, calculatedNow);

  async function storePendingEvent(event: CreateDrinkEventRequest): Promise<void> {
    if (installation === null) {
      await eventQueue.put(event);
      return;
    }
    const stored = await consumptionOutbox.enqueue(event, {
      installationId: installation.installationId,
      membershipIds: installation.memberships.map((membership) => membership.id),
    });
    if (!stored) {
      throw new Error('Das Getränk konnte nicht sicher offline vorgemerkt werden.');
    }
  }

  async function refreshPendingMutations(): Promise<void> {
    const mutations = await drinkMutationQueue.list();
    setPendingMutations(
      mutations.filter(
        (mutation) =>
          identity !== null &&
          mutation.groupId === identity.groupId &&
          mutation.participantId === identity.participantId,
      ),
    );
  }

  async function queueEventUpdate(event: DrinkEvent, correctedEvent: CreateDrinkEventRequest) {
    const stored = await drinkMutationQueue.update({
      eventId: event.id,
      groupId: event.groupId,
      participantId: event.participantId,
      occurredAt: correctedEvent.occurredAt,
      volumeMl: correctedEvent.volumeMl,
      abv: correctedEvent.abv,
    });
    if (!stored) {
      throw new Error('Die Änderung konnte nicht sicher offline vorgemerkt werden.');
    }
    await refreshPendingMutations();
  }

  async function queueEventDeletion(event: DrinkEvent) {
    const stored = await drinkMutationQueue.delete({
      eventId: event.id,
      groupId: event.groupId,
      participantId: event.participantId,
    });
    if (!stored) {
      throw new Error('Das Löschen konnte nicht sicher offline vorgemerkt werden.');
    }
    await refreshPendingMutations();
  }

  async function removePendingEvent(eventId: string): Promise<void> {
    if (installation === null) {
      await eventQueue.remove(eventId);
    } else {
      await consumptionOutbox.remove(eventId);
    }
  }

  async function createPendingEventRemotely(event: CreateDrinkEventRequest): Promise<void> {
    if (installation === null) {
      await api.createDrinkEvent(event);
      return;
    }
    const command = (await consumptionOutbox.list()).find((item) => item.id === event.id);
    if (command === undefined) {
      throw new Error('Die eingefrorene Gruppenauswahl des Getränks fehlt.');
    }
    await api.createConsumption({
      ...event,
      audienceMembershipIds: command.membershipIds,
    });
  }

  async function addDrink(drinkId: CounterDrinkId) {
    if (bootstrap === undefined || addingDrinkRef.current) {
      return;
    }
    const measurement = selectedMeasurements[drinkId];
    const now = new Date();
    const event: CreateDrinkEventRequest = {
      id: crypto.randomUUID(),
      occurredAt: now.toISOString(),
      volumeMl: measurement.volumeMl,
      abv: measurement.abv,
    };
    addingDrinkRef.current = true;
    setAddingDrink(true);
    try {
      await storePendingEvent(event);
      setPending((current) => [...current, event]);
      setLastAddedId(event.id);
      const savedLabel = describeDrink(measurement, GLOBAL_DEFAULT_BEER_ABV);
      setAnnouncement(
        isOnline ? `${savedLabel} wird gespeichert …` : `${savedLabel} jetzt offline vorgemerkt.`,
      );
      navigator.vibrate?.(30);
      if (installation !== null && isOnline) {
        setPhotoError('');
        setPhotoSelectionStarted(false);
        setPhotoCountdownSeconds(60);
        setPhotoOfferExpiresAt(Date.parse(event.occurredAt) + 60_000);
        setPhotoOffer({ eventId: event.id, label: savedLabel });
      }
      if (isOnline) {
        await syncPending();
        setAnnouncement(`${savedLabel} gespeichert.`);
      }
    } catch (error) {
      setAnnouncement(
        error instanceof Error ? error.message : 'Das Getränk konnte nicht gespeichert werden.',
      );
    } finally {
      addingDrinkRef.current = false;
      setAddingDrink(false);
    }
  }

  async function uploadPhoto(file: File): Promise<void> {
    if (photoOffer === null || installation === null || bootstrap === undefined) return;
    if (!isOnline) {
      setPhotoError('Ein Foto kann nur mit Internetverbindung hochgeladen werden.');
      return;
    }
    setPhotoBusy(true);
    setPhotoError('');
    try {
      const prepared = await preparePhoto(file);
      await syncPending();
      await api.uploadPhoto(bootstrap.group.id, photoOffer.eventId, prepared);
      setPhotoOffer(null);
      setPhotoOfferExpiresAt(null);
      setAnnouncement('Foto zum Getränk gespeichert.');
      await queryClient.invalidateQueries({ queryKey: ['photos', bootstrap.group.id] });
      await queryClient.invalidateQueries({
        queryKey: ['photo-upload-status', bootstrap.group.id],
      });
    } catch (error) {
      setPhotoError(
        error instanceof Error ? error.message : 'Das Foto konnte nicht hochgeladen werden.',
      );
    } finally {
      setPhotoBusy(false);
      if (cameraInput.current !== null) cameraInput.current.value = '';
      if (libraryInput.current !== null) libraryInput.current.value = '';
    }
  }

  function startPhotoSelection(input: { current: HTMLInputElement | null }): void {
    setPhotoSelectionStarted(true);
    input.current?.click();
  }

  async function undoLast() {
    if (lastAddedId === null) {
      return;
    }
    if (photoOffer?.eventId === lastAddedId) {
      setPhotoOffer(null);
      setPhotoOfferExpiresAt(null);
    }
    const isPending = pending.some((event) => event.id === lastAddedId);
    if (isPending) {
      await removePendingEvent(lastAddedId);
      setPending((current) => current.filter((event) => event.id !== lastAddedId));
    } else if (!isOnline) {
      const event = personalCombinedEvents.find((item) => item.id === lastAddedId);
      if (event !== undefined) {
        await queueEventDeletion(event);
      }
    } else if (installation !== null) {
      await api.deleteConsumption(lastAddedId);
      await queryClient.invalidateQueries({ queryKey: ['bootstrap'] });
      await queryClient.invalidateQueries({ queryKey: ['consumptions'] });
    } else {
      await api.deleteDrinkEvent(lastAddedId);
      await queryClient.invalidateQueries({ queryKey: ['bootstrap'] });
    }
    setLastAddedId(null);
    setAnnouncement('Letzten Eintrag rückgängig gemacht.');
  }

  async function removeDrinkEvent(event: DrinkEvent) {
    setEventHistoryError('');
    try {
      const isPending = pending.some((item) => item.id === event.id);
      if (isPending) {
        await removePendingEvent(event.id);
        setPending((current) => current.filter((item) => item.id !== event.id));
      } else {
        if (!isOnline) {
          await queueEventDeletion(event);
        } else {
          if (installation === null) {
            await api.deleteDrinkEvent(event.id);
          } else {
            await api.deleteConsumption(event.id);
          }
          await queryClient.invalidateQueries({ queryKey: ['bootstrap'] });
          await queryClient.invalidateQueries({ queryKey: ['consumptions'] });
        }
      }
      setEditingEventId((current) => (current === event.id ? null : current));
      setLastAddedId((current) => (current === event.id ? null : current));
      if (photoOffer?.eventId === event.id) {
        setPhotoOffer(null);
        setPhotoOfferExpiresAt(null);
      }
      setDeletedEvent(event);
      if (deletedEventTimer.current !== null) {
        window.clearTimeout(deletedEventTimer.current);
      }
      deletedEventTimer.current = window.setTimeout(() => setDeletedEvent(null), 10_000);
      setAnnouncement('Getränkeeintrag gelöscht. Rückgängig ist zehn Sekunden lang möglich.');
    } catch (error) {
      setEventHistoryError(deletionMessage(error));
    }
  }

  function startEditing(event: DrinkEvent) {
    if (bootstrap === undefined) {
      return;
    }
    setEditingEventId(event.id);
    setEditingOccurredAt(
      toZonedDateTimeLocalValue(new Date(event.occurredAt), bootstrap.group.timezone),
    );
    setEditingVolumeMl(String(event.volumeMl));
    setEditingAbv(String(event.abv));
    setEditingError('');
  }

  async function saveEditedEvent(event: DrinkEvent) {
    if (bootstrap === undefined) {
      return;
    }
    setSavingEdit(true);
    setEditingError('');
    try {
      const occurredAt = fromZonedDateTimeLocalValue(
        editingOccurredAt,
        bootstrap.group.timezone,
      ).toISOString();
      const volumeMl = Number(editingVolumeMl);
      const abv = Number(editingAbv);
      if (
        !Number.isInteger(volumeMl) ||
        volumeMl < 10 ||
        volumeMl > 5_000 ||
        abv < 0 ||
        abv > 100
      ) {
        throw new Error('Bitte prüfe Menge und Alkoholgehalt.');
      }
      const queuedEvent = pending.find((item) => item.id === event.id);
      if (queuedEvent === undefined) {
        const correctedEvent = { ...event, occurredAt, volumeMl, abv };
        if (!isOnline) {
          await queueEventUpdate(event, correctedEvent);
        } else {
          try {
            if (installation === null) {
              await api.updateDrinkEvent(event.id, { occurredAt, volumeMl, abv });
            } else {
              await api.updateConsumption(event.id, { occurredAt, volumeMl, abv });
            }
            await queryClient.invalidateQueries({ queryKey: ['bootstrap'] });
            await queryClient.invalidateQueries({ queryKey: ['consumptions'] });
          } catch (error) {
            if (!isRetryableSyncError(error)) {
              throw error;
            }
            await queueEventUpdate(event, correctedEvent);
          }
        }
      } else {
        const correctedEvent = { ...queuedEvent, occurredAt, volumeMl, abv };
        await storePendingEvent(correctedEvent);
        setPending((current) =>
          current.map((item) => (item.id === event.id ? correctedEvent : item)),
        );
        if (isOnline) {
          await createPendingEventRemotely(correctedEvent);
          if (installation === null) {
            await api.updateDrinkEvent(event.id, { occurredAt, volumeMl, abv });
          } else {
            await api.updateConsumption(event.id, { occurredAt, volumeMl, abv });
          }
          await removePendingEvent(event.id);
          setPending((current) => current.filter((item) => item.id !== event.id));
          await queryClient.invalidateQueries({ queryKey: ['bootstrap'] });
          await queryClient.invalidateQueries({ queryKey: ['consumptions'] });
        }
      }
      setEditingEventId(null);
      setAnnouncement(
        `Eintrag auf ${describeDrink({ volumeMl, abv }, GLOBAL_DEFAULT_BEER_ABV)} geändert.`,
      );
    } catch (error) {
      setEditingError(
        error instanceof Error ? error.message : 'Der Zeitpunkt konnte nicht geändert werden.',
      );
    } finally {
      setSavingEdit(false);
    }
  }

  async function undoDeletedEvent() {
    if (deletedEvent === null) {
      return;
    }
    if (!pending.some((item) => item.id === deletedEvent.id)) {
      const queuedMutation = pendingMutations.find(
        (mutation) => mutation.eventId === deletedEvent.id,
      );
      if (queuedMutation?.operation === 'delete') {
        await drinkMutationQueue.remove(deletedEvent.id);
        await refreshPendingMutations();
        setDeletedEvent(null);
        setAnnouncement('Das Löschen wurde offline rückgängig gemacht.');
        return;
      }
    }
    const restored: CreateDrinkEventRequest = {
      id: crypto.randomUUID(),
      occurredAt: deletedEvent.occurredAt,
      volumeMl: deletedEvent.volumeMl,
      abv: deletedEvent.abv,
    };
    await storePendingEvent(restored);
    setPending((current) => [...current, restored]);
    setDeletedEvent(null);
    setLastAddedId(restored.id);
    setAnnouncement('Gelöschten Getränkeeintrag wiederhergestellt.');
    if (isOnline) {
      void syncPending();
    }
  }

  async function createNewInvitation(): Promise<GroupInvitation | null> {
    if (bootstrap === undefined) {
      return null;
    }
    setInvitationBusy(true);
    try {
      const result = await api.createInvitation(bootstrap.group.id);
      setInvitation(result);
      setClock(Date.now());
      setAnnouncement('Neuer Einladungscode erstellt. Er ist 30 Minuten gültig.');
      return result;
    } catch (error) {
      setAnnouncement(
        error instanceof Error ? error.message : 'Der Code konnte nicht erstellt werden.',
      );
      return null;
    } finally {
      setInvitationBusy(false);
    }
  }

  async function shareNewInvitation() {
    const nextInvitation = await createNewInvitation();
    if (nextInvitation === null) {
      return;
    }
    const text = `LBC-Gruppencode: ${nextInvitation.code} (30 Minuten gültig)`;
    try {
      if (navigator.share !== undefined) {
        await navigator.share({ title: 'LBC-Einladung', text });
      } else {
        await navigator.clipboard?.writeText(nextInvitation.code);
        setAnnouncement('Neuer Gruppencode kopiert.');
      }
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        setAnnouncement('Die Einladung konnte nicht geteilt werden. Der Code bleibt sichtbar.');
      }
    }
  }

  function updateProfile(profileUpdate: ProfileSettings) {
    setProfile(profileUpdate);
    if (installation !== null) {
      installationStore.write({ ...installation, profile: profileUpdate });
    }
    void queryClient.invalidateQueries({ queryKey: ['bootstrap'] });
  }

  async function saveGroupName() {
    if (installation === null || selectedMembership === null) return;
    const displayName = groupNameDraft.trim();
    if (displayName.length === 0 || displayName.length > 30) {
      setAnnouncement('Der Name muss zwischen 1 und 30 Zeichen lang sein.');
      return;
    }
    setGroupNameBusy(true);
    try {
      const updatedMembership = await api.updateMembershipName(selectedMembership.id, displayName);
      const latest = installationStore.read();
      if (latest !== null) {
        installationStore.write({
          ...latest,
          memberships: latest.memberships.map((membership) =>
            membership.id === updatedMembership.id
              ? { ...membership, displayName: updatedMembership.displayName }
              : membership,
          ),
        });
      }
      setAnnouncement('Dein Gruppenname wurde gespeichert.');
      await queryClient.invalidateQueries({ queryKey: ['bootstrap'] });
      onIdentityChange(activeTab);
    } catch (error) {
      setAnnouncement(
        error instanceof Error ? error.message : 'Der Name konnte nicht gespeichert werden.',
      );
    } finally {
      setGroupNameBusy(false);
    }
  }

  function switchKnownGroup(groupId: string) {
    if (installation !== null) {
      const membership = installation.memberships.find((item) => item.groupId === groupId);
      if (membership !== undefined && installationStore.selectGroup(groupId)) {
        queryClient.clear();
        identityStore.write(membership.legacyIdentity, membership.groupName);
        onIdentityChange(activeTab);
      }
      return;
    }
    if (identityStore.activate(groupId)) {
      queryClient.clear();
      onIdentityChange(activeTab);
    }
  }

  function removeLocalGroupAndContinue(groupId: string) {
    identityStore.remove(groupId);
    const nextGroup = identityStore.readAll()[0];
    if (nextGroup !== undefined && identityStore.activate(nextGroup.identity.groupId)) {
      onIdentityChange();
      return;
    }
    onExit();
  }

  async function leaveCurrentGroup() {
    if (identity === null) {
      return;
    }
    if (
      !window.confirm(
        'Mitgliedschaft wirklich beenden? Bereits geteilte Getränke bleiben in der Gruppe sichtbar. Neue Getränke werden dort nicht mehr geteilt. Um später zurückzukehren, benötigst du einen neuen Einladungslink oder Gruppencode.',
      )
    ) {
      return;
    }
    if (installation !== null && selectedMembership !== null) {
      if (!isOnline) {
        setAnnouncement('Eine Mitgliedschaft kann nur online sicher beendet werden.');
        return;
      }
      try {
        await syncPending();
        if ((await consumptionOutbox.list()).length > 0) {
          setAnnouncement(
            'Die Mitgliedschaft bleibt aktiv, bis alle vorgemerkten Getränke synchronisiert sind.',
          );
          return;
        }
        const remaining = installation.memberships.filter(
          (membership) => membership.id !== selectedMembership.id,
        );
        // Verify local persistence before the irreversible server-side session revocation.
        // The following membership snapshot is smaller than this preflight write.
        if (!installationStore.write(installation)) {
          throw new Error('Der lokale Mitgliedschaftsstand kann gerade nicht gespeichert werden.');
        }
        await api.leaveMembership(selectedMembership.id);
        if (!installationStore.synchronizeMemberships(remaining)) {
          throw new Error(
            'Der bestätigte Mitgliedschaftsstand konnte lokal nicht gespeichert werden.',
          );
        }
        queryClient.clear();
        const next = remaining[0];
        if (next !== undefined) {
          identityStore.write(next.legacyIdentity, next.groupName);
          installationStore.selectGroup(next.groupId);
          onIdentityChange();
        } else {
          identityStore.clear();
          onExit();
        }
      } catch (error) {
        setAnnouncement(
          error instanceof Error
            ? error.message
            : 'Die Mitgliedschaft konnte nicht sicher beendet werden.',
        );
      }
      return;
    }
    try {
      await api.revokeCurrentSession();
    } catch {
      // The local session is removed even when an expired or offline session cannot be revoked.
    }
    queryClient.clear();
    removeLocalGroupAndContinue(identity.groupId);
  }

  function switchPerson() {
    void leaveCurrentGroup();
  }

  if (bootstrap === undefined && bootstrapQuery.isPending) {
    return (
      <main className="loading-screen">
        <Logo />
        <p>Gruppenstand wird geladen …</p>
      </main>
    );
  }

  if (bootstrap === undefined) {
    return (
      <main className="loading-screen">
        <Logo />
        <h1>Der Gruppenstand ist gerade nicht erreichbar.</h1>
        <p role="alert">{readableDashboardError(bootstrapQuery.error)}</p>
        <button className="button button--primary" onClick={() => void bootstrapQuery.refetch()}>
          Erneut versuchen
        </button>
        <button className="button button--text" onClick={switchPerson}>
          Gerät/Person wechseln
        </button>
      </main>
    );
  }

  const groupTotal = activeEvents.length;
  const participantCounts = new Map(
    bootstrap.participants.map((participant) => [
      participant.id,
      activeEvents.filter((event) => event.participantId === participant.id).length,
    ]),
  );
  const participantAlcoholPeaks = new Map(
    bootstrap.alcoholSnapshot.curves.map((curve) => [
      curve.participantId,
      Math.max(curve.currentEstimate, ...curve.points.map((point) => point.estimatedPermille)),
    ]),
  );
  if (alcoholProfile !== null) {
    const ownPeak = Math.max(
      estimate,
      ...createAlcoholCurve(
        groupOwnEvents,
        alcoholProfile,
        drinkingWindow?.start ?? calculatedNow,
        calculatedNow,
      ).map((point) => point.estimatedPermille),
    );
    participantAlcoholPeaks.set(identity.participantId, ownPeak);
  } else if (!participantAlcoholPeaks.has(identity.participantId)) {
    participantAlcoholPeaks.set(identity.participantId, estimate);
  }
  const dailyStandings = createDailyStandings(
    bootstrap.participants,
    participantAlcoholPeaks,
    participantCounts,
  );
  const drinkPresets = createCounterDrinks(GLOBAL_DEFAULT_BEER_ABV).map((drink) => ({
    ...drink,
    ...selectedMeasurements[drink.id],
    volumeLabel: formatCounterVolume(selectedMeasurements[drink.id].volumeMl, drink.id),
    estimatedKilocalories: estimateDrinkKilocalories(selectedMeasurements[drink.id]),
  }));
  const latestSelectableTime = toZonedDateTimeLocalValue(new Date(), bootstrap.group.timezone);
  const activeMemberships = installation?.memberships ?? [];
  const invitationSeconds =
    invitation === null
      ? 0
      : Math.max(0, Math.ceil((new Date(invitation.expiresAt).getTime() - clock) / 1_000));

  const annualRecapVisible = isAnnualWrappedAvailable(bootstrap.group.timezone, calculatedNow);
  const monthlyComparison =
    installation === null
      ? createWrappedMonthlyComparison(
          bootstrap,
          allOwnEvents,
          calculatedNow,
          wrappedMonthlyTargetMonthKey(bootstrap.group.timezone, calculatedNow),
        )
      : null;
  const monthlyRecapVisible =
    !annualRecapVisible && isMonthlyWrappedAvailable(bootstrap.group.timezone, calculatedNow);
  const wrappedPromptPeriodKey = annualRecapVisible
    ? `annual-${wrappedAnnualPeriodKey(bootstrap.group.timezone, calculatedNow)}`
    : monthlyRecapVisible
      ? `monthly-${wrappedMonthlyTargetMonthKey(bootstrap.group.timezone, calculatedNow)}`
      : null;
  const wrappedPromptStatus =
    wrappedPromptPeriodKey === null
      ? null
      : window.localStorage.getItem(`lbc.wrapped.prompt.${wrappedPromptPeriodKey}`);
  const wrappedWasViewed =
    wrappedPromptPeriodKey !== null &&
    (wrappedPromptStatus === 'viewed' ||
      window.localStorage.getItem(`lbc.wrapped.viewed.${wrappedPromptPeriodKey}`) === 'viewed');
  const wrappedPromptVisible =
    wrappedPromptPeriodKey !== null &&
    wrappedPromptPeriodKey !== 'monthly-2026-08' &&
    (installation === null || personalConsumptionsQuery.isSuccess) &&
    wrappedPromptHiddenPeriod !== wrappedPromptPeriodKey &&
    !wrappedWasViewed;

  function closeWrappedPrompt(openWrapped: boolean) {
    if (openWrapped) {
      if (wrappedPromptPeriodKey !== null) {
        window.localStorage.setItem(`lbc.wrapped.pending-reminder.${wrappedPromptPeriodKey}`, '1');
      }
      onOpenWrapped();
    } else {
      setWrappedPromptHiddenPeriod(wrappedPromptPeriodKey);
    }
  }

  return (
    <main className="dashboard">
      <input
        ref={cameraInput}
        className="visually-hidden"
        type="file"
        accept="image/*"
        capture="environment"
        tabIndex={-1}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file !== undefined) void uploadPhoto(file);
        }}
      />
      <input
        ref={libraryInput}
        className="visually-hidden"
        type="file"
        accept="image/*"
        tabIndex={-1}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file !== undefined) void uploadPhoto(file);
        }}
      />
      <header className="dashboard__header">
        <Logo compact />
        <div>
          <p className="eyebrow">{bootstrap.group.name ?? 'Deine Gruppe'}</p>
          <p className="identity-label">Hallo, {bootstrap.participant.displayName}</p>
        </div>
        <button
          className="icon-button activity-feed-button"
          type="button"
          aria-label={
            feedOpen
              ? 'Aktivitätsfeed schließen'
              : feedUnreadCount > 0
                ? `Aktivitätsfeed öffnen, ${feedUnreadCount} neue Aktivitäten verfügbar`
                : 'Aktivitätsfeed öffnen'
          }
          aria-expanded={feedOpen}
          aria-controls="activity-feed"
          onClick={() => setFeedOpen((current) => !current)}
        >
          <svg
            className="activity-feed-icon"
            viewBox="0 0 24 24"
            aria-hidden="true"
            focusable="false"
          >
            <circle cx="6" cy="6" r="1.45" fill="currentColor" />
            <circle
              className="activity-feed-icon__accent"
              cx="6"
              cy="6"
              r="0.78"
              fill="currentColor"
            />
            <path
              d="M10 6h10M10 12h10M10 18h10"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeWidth="1.8"
            />
            <circle cx="6" cy="12" r="1.45" fill="currentColor" />
            <circle cx="6" cy="18" r="1.45" fill="currentColor" />
          </svg>
          {feedUnreadCount > 0 ? (
            <span className="activity-feed-button__unread" aria-hidden="true">
              {feedUnreadCount > 9 ? '9+' : feedUnreadCount}
            </span>
          ) : null}
        </button>
        <button
          className="icon-button settings-button"
          type="button"
          aria-label="Persönliche Einstellungen"
          aria-expanded={settingsOpen}
          aria-controls="personal-settings"
          onClick={() => setSettingsOpen((current) => !current)}
        >
          <svg className="settings-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path
              d="M12 2.8v2.4m0 13.6v2.4M2.8 12h2.4m13.6 0h2.4M5.5 5.5l1.7 1.7m9.6 9.6 1.7 1.7m0-13-1.7 1.7m-9.6 9.6-1.7 1.7"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeWidth="2.6"
            />
            <circle cx="12" cy="12" r="6.7" fill="none" stroke="currentColor" strokeWidth="2.2" />
            <circle cx="12" cy="12" r="2.8" fill="none" stroke="currentColor" strokeWidth="1.8" />
          </svg>
        </button>
      </header>

      {deepPhotoQuery.data !== undefined && dismissedPhotoId !== initialPhotoId ? (
        <PhotoViewer
          key={deepPhotoQuery.data.id}
          photo={deepPhotoQuery.data}
          onClose={() => {
            setDismissedPhotoId(deepPhotoQuery.data?.id ?? initialPhotoId);
            onClosePhoto();
          }}
        />
      ) : null}

      {(monthlyRecapVisible || annualRecapVisible) && wrappedPromptVisible ? (
        <div className="wrapped-prompt-backdrop" role="presentation">
          <section
            className="wrapped-prompt"
            role="dialog"
            aria-modal="true"
            aria-labelledby="wrapped-prompt-title"
          >
            <span className="wrapped-prompt__spark" aria-hidden="true">
              ✦
            </span>
            <p className="step-label">
              {annualRecapVisible ? 'Dein Jahresrückblick ist da' : 'Dein Monatsrückblick ist da'}
            </p>
            <h2 id="wrapped-prompt-title">LBC Wrapped</h2>
            <p>
              {annualRecapVisible
                ? 'Der Jahresrückblick deiner Gruppe ist bereit.'
                : `Deine persönliche Auswertung für ${new Intl.DateTimeFormat('de-DE', {
                    month: 'long',
                    year: 'numeric',
                    timeZone: bootstrap.group.timezone,
                  }).format(
                    new Date(
                      `${wrappedMonthlyTargetMonthKey(bootstrap.group.timezone, calculatedNow)}-01T12:00:00Z`,
                    ),
                  )} ist bereit.`}
            </p>
            {annualRecapVisible || monthlyComparison === null ? null : (
              <>
                <div className="wrapped-prompt__stats">
                  <span>
                    <strong>{monthlyComparison.current.totalDrinks}</strong> Getränke
                  </span>
                  <span>
                    <strong>{monthlyComparison.current.drinkingDays}</strong> Trinktage
                  </span>
                </div>
              </>
            )}
            <div className="wrapped-prompt__actions">
              <button
                className="button button--primary"
                type="button"
                onClick={() => closeWrappedPrompt(true)}
              >
                Jetzt anzeigen
              </button>
              <button
                className="button button--text"
                type="button"
                onClick={() => closeWrappedPrompt(false)}
              >
                Später
              </button>
            </div>
          </section>
        </div>
      ) : null}

      {!isOnline ? (
        <div className="connection-banner" role="status">
          <span aria-hidden="true">○</span>
          Offline · Deine Einträge gehen nicht verloren.
        </div>
      ) : null}

      {bootstrapQuery.isError ? (
        <p className="form-error" role="status">
          Der letzte erfolgreiche Gruppenstand wird angezeigt.{' '}
          {readableDashboardError(bootstrapQuery.error)}
        </p>
      ) : null}

      {settingsOpen && profile !== null ? (
        <div id="personal-settings">
          <SettingsPanel
            key={`${profile.weightKg}-${profile.heightCm ?? ''}-${profile.notificationsEnabled === true}`}
            profile={profile}
            groupId={bootstrap.group.id}
            groupName={bootstrap.group.name}
            showWrappedButton={annualRecapVisible}
            onOpenWrapped={onOpenWrapped}
            onProfileUpdated={updateProfile}
            onAnnouncement={setAnnouncement}
          />
        </div>
      ) : null}

      {feedOpen ? (
        <ActivityFeed memberships={activeMemberships} onViewed={acknowledgeFeed} />
      ) : null}

      <nav className="dashboard-tabs" role="tablist" aria-label="Bereich auswählen">
        <button
          id="today-tab"
          type="button"
          role="tab"
          aria-selected={activeTab === 'today'}
          aria-controls="today-panel"
          onClick={() => setActiveTab('today')}
        >
          Heute
        </button>
        {installation === null ? (
          <button
            id="group-tab"
            type="button"
            role="tab"
            aria-selected={activeTab === 'group'}
            aria-controls="group-panel"
            onClick={() => setActiveTab('group')}
          >
            Gruppe
          </button>
        ) : (
          <GroupViewSelector
            memberships={activeMemberships}
            selectedGroupId={selectedMembership?.groupId ?? ''}
            active={activeTab === 'group'}
            onAddGroup={onAddGroup}
            onOpenGroup={() => setActiveTab('group')}
            onSelectGroup={switchKnownGroup}
          />
        )}
      </nav>

      {activeTab === 'today' ? (
        <div id="today-panel" role="tabpanel" aria-labelledby="today-tab">
          <section className="counter-card" aria-labelledby="own-counter-title">
            <div className="counter-summary">
              <div className="counter-summary__drinks">
                <p className="step-label" id="own-counter-title">
                  Dein Trinktag
                </p>
                <div className="counter-value">
                  <strong>{ownCount}</strong>
                  <span>{ownCount === 1 ? 'Getränk' : 'Getränke'}</span>
                </div>
              </div>
              <div className="counter-summary__metrics">
                <div className="counter-metric">
                  <span>Aktueller Promillewert</span>
                  <strong>ca. {estimate.toFixed(2)} ‰</strong>
                </div>
                <div className="counter-metric">
                  <span>Kalorien</span>
                  <strong>ca. {formatKilocalories(ownKilocalories)} kcal</strong>
                </div>
              </div>
            </div>
            <div className="counter-controls">
              <p className="capture-now-note">
                Jeder Tap wird mit dem aktuellen Zeitpunkt erfasst.
                {installation === null
                  ? null
                  : ` Wird mit ${activeMemberships.length} ${
                      activeMemberships.length === 1 ? 'aktiven Gruppe' : 'aktiven Gruppen'
                    } geteilt.`}
              </p>
              <div className="drink-actions" aria-label="Getränk hinzufügen">
                {drinkPresets.map((preset) => (
                  <div className={`drink-tile drink-tile--${preset.id}`} key={preset.id}>
                    <button
                      className={`drink-button drink-button--${preset.id}`}
                      type="button"
                      disabled={addingDrink}
                      aria-label={`${preset.label} hinzufügen, ${preset.abv.toLocaleString('de-DE')} Prozent Alkohol, ungefähr ${formatKilocalories(preset.estimatedKilocalories)} Kilokalorien${preset.id === 'cocktail' ? ' plus Mixer' : ''}`}
                      onClick={() => void addDrink(preset.id)}
                    >
                      <span aria-hidden="true">{addingDrink ? '…' : '＋'}</span>
                      <strong>{preset.volumeLabel}</strong>
                      <small>{preset.shortLabel}</small>
                      <em>{preset.abv.toLocaleString('de-DE')} % vol</em>
                      <span className="drink-button__calories">
                        ca. {formatKilocalories(preset.estimatedKilocalories)} kcal
                        {preset.id === 'cocktail' ? ' + Mixer' : ''}
                      </span>
                    </button>
                    <button
                      className="drink-quantity-button"
                      type="button"
                      disabled={addingDrink}
                      aria-expanded={quantityPicker === preset.id}
                      onClick={() => {
                        setQuantityPicker((current) => (current === preset.id ? null : preset.id));
                        if (preset.id === 'cocktail' && quantityPicker !== 'cocktail') {
                          setCocktailVolumeDraft(
                            String(selectedMeasurements.cocktail.volumeMl / 10),
                          );
                          setCocktailAbvDraft(String(selectedMeasurements.cocktail.abv));
                        }
                      }}
                    >
                      Menge
                    </button>
                  </div>
                ))}
              </div>
              {quantityPicker !== null ? (
                <div
                  role="group"
                  className="drink-quantity-picker"
                  aria-label={`${quantityPicker} Menge wählen`}
                >
                  {quantityPicker === 'beer' ? (
                    <>
                      <strong>Biermenge</strong>
                      <div>
                        {[330, 500, 1_000].map((volumeMl) => (
                          <button
                            type="button"
                            key={volumeMl}
                            className={
                              selectedMeasurements.beer.volumeMl === volumeMl ? 'is-selected' : ''
                            }
                            onClick={() => {
                              setSelectedMeasurements((current) => ({
                                ...current,
                                beer: { ...current.beer, volumeMl },
                              }));
                              setQuantityPicker(null);
                            }}
                          >
                            {formatCounterVolume(volumeMl, 'beer')}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : quantityPicker === 'wine' ? (
                    <>
                      <strong>Weinmenge</strong>
                      <div>
                        {[100, 200].map((volumeMl) => (
                          <button
                            type="button"
                            key={volumeMl}
                            className={
                              selectedMeasurements.wine.volumeMl === volumeMl ? 'is-selected' : ''
                            }
                            onClick={() => {
                              setSelectedMeasurements((current) => ({
                                ...current,
                                wine: { ...current.wine, volumeMl },
                              }));
                              setQuantityPicker(null);
                            }}
                          >
                            {formatCounterVolume(volumeMl, 'wine')}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <strong>Cocktail anpassen</strong>
                      <label>
                        Menge in cl
                        <input
                          type="text"
                          inputMode="decimal"
                          value={cocktailVolumeDraft}
                          onChange={(event) => setCocktailVolumeDraft(event.target.value)}
                          onFocus={moveCursorToEnd}
                        />
                      </label>
                      <label>
                        Alkohol in % vol
                        <input
                          type="text"
                          inputMode="decimal"
                          value={cocktailAbvDraft}
                          onChange={(event) => setCocktailAbvDraft(event.target.value)}
                          onFocus={moveCursorToEnd}
                        />
                      </label>
                      <button
                        type="button"
                        disabled={
                          parseCocktailDraft(cocktailVolumeDraft, 1, 200) === null ||
                          parseCocktailDraft(cocktailAbvDraft, 0.1, 100) === null
                        }
                        onClick={() => {
                          const volumeCl = parseCocktailDraft(cocktailVolumeDraft, 1, 200);
                          const abv = parseCocktailDraft(cocktailAbvDraft, 0.1, 100);
                          if (volumeCl === null || abv === null) return;
                          setSelectedMeasurements((current) => ({
                            ...current,
                            cocktail: { volumeMl: volumeCl * 10, abv },
                          }));
                          setQuantityPicker(null);
                        }}
                      >
                        Übernehmen
                      </button>
                    </>
                  )}
                </div>
              ) : null}
            </div>
          </section>

          <div className="counter-actions">
            {lastAddedId !== null ? (
              <button className="undo-button" type="button" onClick={() => void undoLast()}>
                ↶ Rückgängig
              </button>
            ) : (
              <span />
            )}
            <p className={`sync-state ${pending.length > 0 ? 'sync-state--pending' : ''}`}>
              <span aria-hidden="true">●</span>{' '}
              {syncing
                ? 'Synchronisiert …'
                : pending.length > 0
                  ? `${pending.length} ausstehend`
                  : isOnline
                    ? 'Aktuell'
                    : 'Offline'}
            </p>
          </div>

          {photoOffer !== null ? (
            <aside className="photo-offer" role="status" aria-live="polite">
              <div className="photo-offer__header">
                <div>
                  <strong>Foto zu {photoOffer.label}?</strong>
                  <span>
                    Wähle jetzt Kamera oder Mediathek. Danach bleibt der Eintrag ohne Foto.
                  </span>
                </div>
                {!photoSelectionStarted ? (
                  <div
                    className="photo-offer__countdown"
                    role="timer"
                    aria-label={`Noch ${photoCountdownSeconds} Sekunden für das Foto`}
                  >
                    <svg viewBox="0 0 44 44" aria-hidden="true" focusable="false">
                      <circle className="photo-offer__countdown-track" cx="22" cy="22" r="18" />
                      <circle
                        className="photo-offer__countdown-progress"
                        cx="22"
                        cy="22"
                        r="18"
                        style={{ strokeDashoffset: `${113.1 * (1 - photoCountdownSeconds / 60)}` }}
                      />
                    </svg>
                    <strong>{photoCountdownSeconds}</strong>
                  </div>
                ) : null}
              </div>
              <div className="photo-offer__actions">
                <button
                  className="button button--secondary"
                  type="button"
                  disabled={photoBusy}
                  onClick={() => startPhotoSelection(cameraInput)}
                >
                  Kamera
                </button>
                <button
                  className="button button--secondary"
                  type="button"
                  disabled={photoBusy}
                  onClick={() => startPhotoSelection(libraryInput)}
                >
                  Mediathek
                </button>
              </div>
              <small>
                {photoUploadStatusQuery.data?.dailyUnlimited
                  ? 'Heute sind für dich unbegrenzt viele Fotos möglich.'
                  : photoUploadStatusQuery.isPending
                    ? 'Heute verbleibende Fotos werden geprüft …'
                    : `Heute noch ${photoUploadStatusQuery.data?.dailyRemaining ?? '—'} von ${photoUploadStatusQuery.data?.dailyLimit ?? 3} Fotos möglich.`}
              </small>
              {photoSelectionStarted && !photoBusy ? <small>Auswahl läuft …</small> : null}
              {photoBusy ? <small>Foto wird optimiert und hochgeladen …</small> : null}
              {photoError !== '' ? <small className="form-error">{photoError}</small> : null}
            </aside>
          ) : null}

          <details className="event-history" aria-labelledby="event-history-title">
            <summary className="event-history__summary">
              <span>
                <span className="step-label">Nachtragen und korrigieren</span>
                <strong id="event-history-title">Deine letzten Einträge</strong>
              </span>
              <span className="people-count">{bootstrap.group.timezone}</span>
            </summary>
            <div className="event-history__content">
              {eventHistoryError === '' ? null : (
                <p className="form-error" role="alert">
                  {eventHistoryError}
                </p>
              )}
              {recentOwnEvents.length === 0 ? (
                <p className="empty-state">Noch keine Getränke eingetragen.</p>
              ) : (
                <ol className="event-list">
                  {recentOwnEvents.map((event) => (
                    <li className="event-item" key={event.id}>
                      <div className="event-item__summary">
                        <div>
                          <strong>{describeDrink(event, GLOBAL_DEFAULT_BEER_ABV)}</strong>
                          <span className="event-item__calories">
                            ca. {formatKilocalories(estimateDrinkKilocalories(event))} kcal
                            {isCocktailPreset(event) ? ' + Mixer' : ''}
                          </span>
                          <time dateTime={event.occurredAt}>
                            {formatEventTime(event.occurredAt, bootstrap.group.timezone)}
                          </time>
                        </div>
                        {event.syncState === 'pending' ? (
                          <span className="event-pending">Ausstehend</span>
                        ) : null}
                        <button type="button" onClick={() => startEditing(event)}>
                          Bearbeiten
                        </button>
                        <button type="button" onClick={() => void removeDrinkEvent(event)}>
                          Eintrag löschen
                        </button>
                      </div>
                      {editingEventId === event.id ? (
                        <form
                          className="event-edit-form"
                          onSubmit={(submitEvent) => {
                            submitEvent.preventDefault();
                            void saveEditedEvent(event);
                          }}
                        >
                          <label>
                            Getränketyp
                            <select
                              value={presetValue(
                                Number(editingVolumeMl),
                                Number(editingAbv),
                                GLOBAL_DEFAULT_BEER_ABV,
                              )}
                              onChange={(inputEvent) => {
                                const preset = historyPresets(GLOBAL_DEFAULT_BEER_ABV).find(
                                  (item) => item.id === inputEvent.target.value,
                                );
                                if (preset !== undefined) {
                                  setEditingVolumeMl(String(preset.volumeMl));
                                  setEditingAbv(String(preset.abv));
                                }
                              }}
                            >
                              <option value="custom">Freie Eingabe</option>
                              {historyPresets(GLOBAL_DEFAULT_BEER_ABV).map((preset) => (
                                <option value={preset.id} key={preset.id}>
                                  {preset.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <div className="event-edit-fields">
                            <label>
                              Menge in ml
                              <input
                                type="number"
                                min={10}
                                max={5000}
                                step={1}
                                value={editingVolumeMl}
                                onChange={(inputEvent) =>
                                  setEditingVolumeMl(inputEvent.target.value)
                                }
                              />
                            </label>
                            <label>
                              Alkohol in % vol
                              <input
                                type="number"
                                min={0}
                                max={100}
                                step={0.1}
                                value={editingAbv}
                                onChange={(inputEvent) => setEditingAbv(inputEvent.target.value)}
                              />
                            </label>
                          </div>
                          <label>
                            Datum und Uhrzeit
                            <input
                              type="datetime-local"
                              value={editingOccurredAt}
                              max={latestSelectableTime}
                              required
                              onChange={(inputEvent) =>
                                setEditingOccurredAt(inputEvent.target.value)
                              }
                            />
                          </label>
                          {editingError === '' ? null : (
                            <p className="form-error" role="alert">
                              {editingError}
                            </p>
                          )}
                          <div className="event-edit-actions">
                            <button
                              className="button button--primary"
                              type="submit"
                              disabled={savingEdit}
                            >
                              Speichern
                            </button>
                            <button
                              className="button button--text"
                              type="button"
                              disabled={savingEdit}
                              onClick={() => setEditingEventId(null)}
                            >
                              Abbrechen
                            </button>
                          </div>
                        </form>
                      ) : null}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </details>

          {deletedEvent !== null ? (
            <div className="undo-toast" role="status">
              <span>Eintrag gelöscht.</span>
              <button type="button" onClick={() => void undoDeletedEvent()}>
                Rückgängig
              </button>
            </div>
          ) : null}

          {monthlyRecapVisible || annualRecapVisible ? (
            <button
              className="button button--secondary group-settings__wrapped"
              type="button"
              onClick={onOpenWrapped}
            >
              {wrappedButtonLabel(bootstrap.group.timezone, calculatedNow)}
            </button>
          ) : null}
          <button className="responsibility-banner" type="button" onClick={showCompliment}>
            Alkohol bewusst genießen
          </button>
        </div>
      ) : (
        <div id="group-panel" role="tabpanel" aria-labelledby="group-tab">
          {isOnline ? (
            <Suspense fallback={<p className="chart-empty">Verlauf wird geladen...</p>}>
              <GroupHistoryChart
                groupId={bootstrap.group.id}
                snapshot={bootstrap.alcoholSnapshot}
                weeklySnapshot={bootstrap.weeklyAlcoholSnapshot}
                participants={bootstrap.participants}
                events={groupCombinedEvents}
                defaultBeerAbv={GLOBAL_DEFAULT_BEER_ABV}
                timezone={bootstrap.group.timezone}
                dayBoundaryHour={bootstrap.group.dayBoundaryHour}
                photosEnabled={installation !== null}
                loadForecast={() =>
                  installation === null
                    ? api.bootstrap()
                    : api.groupBootstrap(bootstrap.group.id, true)
                }
              />
            </Suspense>
          ) : (
            <section className="offline-group-note" aria-label="Gruppendaten offline">
              <strong>Gruppenverlauf pausiert offline.</strong>
              <span>
                Der zuletzt geladene Gruppenstand und deine neuen Einträge bleiben sichtbar.
              </span>
            </section>
          )}

          <section className="group-summary" aria-labelledby="group-summary-title">
            <div className="section-heading">
              <div>
                <p className="step-label">Heute in der Gruppe</p>
                <h2 id="group-summary-title">
                  {groupTotal} {groupTotal === 1 ? 'Getränk' : 'Getränke'} insgesamt
                </h2>
                <p className="group-summary__ranking-note">
                  Kronen nach dem höchsten geschätzten Alkoholpegel des Trinktags
                </p>
              </div>
              <span className="people-count">{bootstrap.participants.length} Personen</span>
            </div>
            <div className="participant-grid">
              {dailyStandings.map(({ participant, count, estimatedPermille, rank }) => (
                <article className="participant-card" key={participant.id}>
                  <div className="participant-card__avatar">
                    {isCrownRank(rank) ? <DailyCrown rank={rank} /> : null}
                    <span className={`avatar avatar--${participant.colorKey ?? 'sky'}`}>
                      {participant.displayName.slice(0, 1).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h3>{participant.displayName}</h3>
                    <p>{participant.id === identity.participantId ? 'Du' : 'Gruppenmitglied'}</p>
                  </div>
                  <div className="participant-card__metrics">
                    <strong>{estimatedPermille.toFixed(2)} ‰</strong>
                    <span>
                      {count} {count === 1 ? 'Getränk' : 'Getränke'}
                    </span>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <details className="group-settings">
            <summary>
              <span>
                <span className="step-label">Diese Gruppe</span>
                <strong>Gruppeneinstellungen</strong>
                <small>Freunde einladen</small>
              </span>
              <span aria-hidden="true">＋</span>
            </summary>
            <div className="group-settings__content">
              {installation !== null && selectedMembership !== null ? (
                <form
                  className="group-name-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void saveGroupName();
                  }}
                >
                  <label>
                    Dein Name in dieser Gruppe
                    <input
                      value={groupNameDraft}
                      maxLength={30}
                      onChange={(event) => setGroupNameDraft(event.target.value)}
                    />
                  </label>
                  <button
                    className="button button--secondary"
                    type="submit"
                    disabled={groupNameBusy}
                  >
                    {groupNameBusy ? 'Wird gespeichert …' : 'Namen speichern'}
                  </button>
                </form>
              ) : null}
              <div className="invitation-card">
                <div>
                  <span>Temporärer Gruppencode</span>
                  {invitation === null || invitationSeconds === 0 ? (
                    <strong>Noch nicht erzeugt</strong>
                  ) : (
                    <>
                      <strong>{invitation.code}</strong>
                      <small>Noch {formatCountdown(invitationSeconds)} gültig</small>
                    </>
                  )}
                </div>
                <div className="invitation-actions">
                  <button
                    type="button"
                    disabled={invitationBusy}
                    onClick={() => void createNewInvitation()}
                  >
                    Neuen Code erzeugen
                  </button>
                  {invitation !== null && invitationSeconds > 0 ? (
                    <button
                      type="button"
                      onClick={() => void navigator.clipboard?.writeText(invitation.code)}
                    >
                      Kopieren
                    </button>
                  ) : null}
                  <button
                    type="button"
                    disabled={invitationBusy}
                    onClick={() => void shareNewInvitation()}
                  >
                    Teilen
                  </button>
                </div>
              </div>
              {isPhotoStorageAdmin ? <PhotoStorageAdminPanel groupId={bootstrap.group.id} /> : null}
              <button
                className="button button--text group-settings__leave"
                type="button"
                onClick={() => void leaveCurrentGroup()}
              >
                Gruppe verlassen
              </button>
            </div>
          </details>

          <AchievementsList
            group={bootstrap.group}
            participants={bootstrap.participants}
            participant={bootstrap.participant}
            events={groupCombinedEvents}
          />

          <aside className="model-notice">
            <strong>Reine Schätzung.</strong> Nicht zur Beurteilung der Fahrtüchtigkeit oder
            gesundheitlichen Sicherheit geeignet. Im Zweifel nicht fahren und keinen weiteren
            Alkohol trinken.
          </aside>
        </div>
      )}

      {complimentVisible ? (
        <div className="compliment-toast" role="status" aria-live="polite">
          Du bist ein geiler Typ.
        </div>
      ) : null}

      <p className="sr-only" aria-live="polite">
        {announcement}
      </p>
    </main>
  );
}

function personalDrinkEvents(
  event: ConsumptionEvent,
  preferredGroupId: string,
  preferredParticipantId: string,
  group?: { timezone: string; dayBoundaryHour: number },
): DrinkEvent[] {
  const candidateProjections = event.projections.some(
    (item) => item.participantId === preferredParticipantId,
  )
    ? event.projections.filter((item) => item.participantId === preferredParticipantId)
    : event.projections;
  const projection =
    [...candidateProjections]
      .sort((left, right) =>
        `${left.groupId}:${left.participantId}:${left.drinkEventId}`.localeCompare(
          `${right.groupId}:${right.participantId}:${right.drinkEventId}`,
        ),
      )
      .find((item) => item.groupId === preferredGroupId) ??
    [...candidateProjections].sort((left, right) =>
      `${left.groupId}:${left.drinkEventId}`.localeCompare(
        `${right.groupId}:${right.drinkEventId}`,
      ),
    )[0];
  if (projection === undefined) return [];
  return [
    {
      id: event.id,
      groupId: projection.groupId,
      participantId: projection.participantId,
      occurredAt: event.occurredAt,
      volumeMl: event.volumeMl,
      abv: event.abv,
      createdAt: event.createdAt,
      updatedAt: event.updatedAt,
      deletedAt: event.deletedAt,
      ...(group === undefined
        ? {}
        : {
            wrappedTimezone: group.timezone,
            wrappedDayBoundaryHour: group.dayBoundaryHour,
          }),
    },
  ];
}

function deletionMessage(error: unknown): string {
  if (error instanceof ApiClientError) {
    return error.message;
  }
  if (error instanceof TypeError) {
    return 'Der Server ist gerade nicht erreichbar. Bitte versuche es erneut.';
  }
  return 'Das Löschen ist fehlgeschlagen. Bitte versuche es erneut.';
}

function isRetryableSyncError(error: unknown): boolean {
  return (
    error instanceof TypeError ||
    (error instanceof ApiClientError && (error.code === 'NETWORK_ERROR' || error.status === 0))
  );
}

function readableDashboardError(error: unknown): string {
  if (error instanceof ApiClientError) {
    return error.message;
  }
  return 'Der Server ist gerade nicht erreichbar. Bitte versuche es erneut.';
}

function mergeEvents(
  serverEvents: DrinkEvent[],
  pending: CreateDrinkEventRequest[],
  mutations: PendingDrinkMutation[],
  groupId: string,
  participantId: string,
): DrinkEvent[] {
  const events = new Map(serverEvents.map((event) => [event.id, event]));
  for (const event of pending) {
    if (!events.has(event.id)) {
      events.set(event.id, {
        ...event,
        groupId,
        participantId,
        createdAt: event.occurredAt,
        updatedAt: event.occurredAt,
        deletedAt: null,
        syncState: 'pending',
      });
    }
  }
  for (const mutation of mutations) {
    const event = events.get(mutation.eventId);
    if (
      event === undefined ||
      mutation.groupId !== groupId ||
      mutation.participantId !== participantId
    ) {
      continue;
    }
    if (mutation.operation === 'delete') {
      events.delete(mutation.eventId);
      continue;
    }
    events.set(mutation.eventId, {
      ...event,
      occurredAt: mutation.occurredAt,
      volumeMl: mutation.volumeMl,
      abv: mutation.abv,
      updatedAt: mutation.occurredAt,
      syncState: 'pending',
    });
  }
  return [...events.values()];
}

function formatEventTime(occurredAt: string, timezone: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: timezone,
  }).format(new Date(occurredAt));
}

function eventRecency(event: DrinkEvent): number {
  return event.syncState === 'pending'
    ? Number.MAX_SAFE_INTEGER
    : new Date(event.updatedAt).getTime();
}

function parseCocktailDraft(draft: string, min: number, max: number): number | null {
  const normalized = draft.trim().replace(',', '.');
  if (!/^(?:\d+(?:\.\d+)?|\.\d+)$/.test(normalized)) return null;
  const value = Number(normalized);
  return Number.isFinite(value) && value >= min && value <= max ? value : null;
}

function moveCursorToEnd(event: FocusEvent<HTMLInputElement>): void {
  const input = event.currentTarget;
  input.setSelectionRange(input.value.length, input.value.length);
}

const FEED_SEEN_KEY_PREFIX = 'lbc.feed.seen.v1.';

function readFeedSeenAt(installationId: string): string | null {
  try {
    return window.localStorage.getItem(`${FEED_SEEN_KEY_PREFIX}${installationId}`);
  } catch {
    return null;
  }
}

function writeFeedSeenAt(installationId: string, createdAt: string): void {
  try {
    window.localStorage.setItem(`${FEED_SEEN_KEY_PREFIX}${installationId}`, createdAt);
  } catch {
    // Local persistence is an enhancement; feed access must remain available without it.
  }
}

function latestFeedCreatedAt(entries: Array<{ event: { createdAt: string } }>): string | undefined {
  return entries.reduce<string | undefined>(
    (latest, entry) =>
      latest === undefined || entry.event.createdAt > latest ? entry.event.createdAt : latest,
    undefined,
  );
}

function feedDayRange(timezone: string): { from: string; to: string } {
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
    .formatToParts(new Date())
    .reduce<Record<string, string>>((parts, part) => {
      if (part.type !== 'literal') parts[part.type] = part.value;
      return parts;
    }, {});
  const date = `${today.year}-${today.month}-${today.day}`;
  const nextDay = new Date(`${date}T00:00:00Z`);
  nextDay.setUTCDate(nextDay.getUTCDate() + 1);
  const nextDate = nextDay.toISOString().slice(0, 10);
  return {
    from: fromZonedDateTimeLocalValue(`${date}T00:00`, timezone).toISOString(),
    to: new Date(
      fromZonedDateTimeLocalValue(`${nextDate}T00:00`, timezone).getTime() - 1,
    ).toISOString(),
  };
}

function formatCounterVolume(volumeMl: number, drinkId: CounterDrinkId): string {
  if (drinkId === 'cocktail') return `${(volumeMl / 10).toLocaleString('de-DE')} cl`;
  return `${(volumeMl / 1_000).toLocaleString('de-DE')} l`;
}

function formatKilocalories(value: number): string {
  return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(value);
}

function historyPresets(beerAbv: number) {
  return [
    { id: 'beer-500', label: '0,5 l Bier', volumeMl: 500, abv: beerAbv },
    { id: 'beer-1000', label: '1,0 l Bier', volumeMl: 1000, abv: beerAbv },
    { id: 'beer-330', label: '0,33 l Bier', volumeMl: 330, abv: beerAbv },
    { id: 'wine-100', label: '0,1 l Wein', volumeMl: 100, abv: 12 },
    { id: 'wine-200', label: '0,2 l Wein', volumeMl: 200, abv: 12 },
    { id: 'cocktail', label: 'Cocktail', volumeMl: 60, abv: 40 },
  ] as const;
}

function presetValue(volumeMl: number, abv: number, beerAbv: number): string {
  return (
    historyPresets(beerAbv).find(
      (preset) => preset.volumeMl === volumeMl && Math.abs(preset.abv - abv) < 0.001,
    )?.id ?? 'custom'
  );
}

function formatCountdown(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}
